import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { DefaultFilter } from '../../../../components/common/table/default-filter';
import ExcelButton from '../../../../components/common/table/excel-button/excel-button';
import Table from '../../../../components/common/table/table';
import { FieldHistoryTabs } from '../../../../enums/field-history-tabs';
import FieldTypes from '../../../../enums/field-types';
import { FieldHistoryTable } from '../../../../interfaces/field-history';
import { CROP_FIELD_HISTORY_VIEW } from '../../../../router/routes';
import { useService } from '../../../../services/services';
import { route } from '../../../../services/utils';
import css from './index.module.css';
import { AjaxResponse } from 'rxjs/ajax';
import InfoModal from '../../../../components/modals/info-modal';

export default function FieldHistoryTableIndex() {
  const { t } = useTranslation();
  const { fieldService } = useService();
  const { farmId, season } = useParams<{ farmId: string; season: string }>();
  const selectedFieldsState = useState<FieldHistoryTable[]>([]);

  const transKeyPrefix = 'pages.crop.field-history.fields.';
  const tp = useCallback(
    (key: string, params?: any) => t(transKeyPrefix.concat(key), params),
    [t]
  );

  const [cropFilterItems, setCropFilterItems] = useState([]);

  const [infoModal, setInfoModal] = useState(null);

  const request = useCallback(
    (page: number, sort: string, desc: boolean, filter: any) =>
      fieldService.historyTable(farmId, season, {
        page,
        sort,
        desc,
        ...filter,
      }),
    [fieldService, farmId, season]
  );

  const toolbar = useCallback(() => {
    const selectedFieldIds = selectedFieldsState[0].map((field) => field.id);
    return (
      <div>
        <div className={css['topbar']}>
          <div className={css['date-with-export']}>
            <div className={css['merge_split_buttons']}>
              <ExcelButton
                onClick={() => {
                  return fieldService
                    .exportFieldHistoryXls(farmId, season, {
                      fields: selectedFieldIds,
                    })
                    .subscribe((response: AjaxResponse) => {
                      if (!response) {
                        setInfoModal(
                          <InfoModal
                            title={tp('data-export')}
                            content={tp('no_data_in_period', {
                              season: season,
                            })}
                            onClose={() => setInfoModal(null)}
                          />
                        );
                        return;
                      }
                      return response;
                    });
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }, [farmId, season, fieldService, selectedFieldsState, tp]);

  useEffect(() => {
    const subscription = fieldService
      .historyTableUsedProductCategories(farmId, season)
      .subscribe((values) => {
        setCropFilterItems(values);
      });
    return () => subscription.unsubscribe();
  }, [fieldService, farmId, season, setCropFilterItems]);

  return (
    <div className={css['body']}>
      <Table<FieldHistoryTable>
        request={request}
        toolbar={toolbar}
        keyAccessor="id"
        columns={[
          {
            accessor: 'lad_number',
            header: tp('lad_number'),
            className: css['strong'],
          },
          {
            accessor: 'name',
            header: t('name'),
            sortable: true,
            className: css['strong'],
          },
          {
            accessor: 'area',
            header: tp('area'),
            sortable: true,
          },
          {
            accessor: 'foregrowth',
            header: tp('foregrowth'),
          },
          {
            accessor: 'crop',
            dataType: FieldTypes.JSON_AGG,
            header: tp('crop'),
            sortable: true,
            filter: (
              <DefaultFilter options={cropFilterItems} name="category_id" />
            ),
          },
          {
            accessor: 'breed',
            dataType: FieldTypes.JSON_AGG,
            header: tp('breed'),
          },
        ]}
        viewRoute={route(CROP_FIELD_HISTORY_VIEW, {
          farmId: farmId,
          tab: FieldHistoryTabs.WORK,
          season: season,
        })}
        viewRecordAbility={{ title: 'view field' }}
        selectedRowsState={selectedFieldsState}
      />
      {infoModal}
    </div>
  );
}
